import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SearchIcon, StarIcon } from 'lucide-react';
import { heroImage, stats } from '../../lib/data';
import { Eyebrow } from '../ui/Reveal';
export function Hero() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/opportunities${query ? `?q=${encodeURIComponent(query)}` : ''}`);
  };
  return <section className="relative overflow-hidden" data-id="element-41" data-mp="5p78u3za7trs">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-16 pt-10 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-24 lg:pt-16" data-id="element-42" data-mp="5rtic95qgjcn">
        <div className="lg:col-span-6" data-id="element-43" data-mp="3rx6z9n66afzu">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-44" data-mp="3ryhzjd0et5m7">
            <Eyebrow data-id="element-45" data-mp="1q5tt6usgy227">Global Executive Search</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl leading-[1.08] text-ink sm:text-5xl lg:text-6xl" data-id="element-46" data-mp="1mwm24026hs6h">
              The leaders who
              <br data-id="element-47" data-mp="1rw728dt346ks" />
              <span className="italic text-gold-dark" data-id="element-48" data-mp="1rwqlpyydv30x">define</span> what comes next.
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-ink-600" data-id="element-49" data-mp="1czzjzj18lf85">
              For four decades, boards and founders have trusted Ardent to find the
              exceptional people who shape their future — discreetly, and without
              compromise.
            </p>

            <form onSubmit={submit} className="mt-8 flex max-w-md items-center rounded-full border border-ink/15 bg-ivory-50 p-1.5 shadow-soft focus-within:border-gold" data-id="element-50" data-mp="2aldogat37gbc">
              <SearchIcon className="ml-3 h-5 w-5 shrink-0 text-ink-500" data-id="element-51" data-mp="20uappfnmrdf8" />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search roles, disciplines, companies…" aria-label="Search opportunities" className="w-full bg-transparent px-3 py-2.5 text-sm text-ink placeholder:text-ink-400 focus:outline-none" data-id="element-52" data-mp="2okwk444poz6r" />
              <button type="submit" className="shrink-0 rounded-full bg-ink px-6 py-2.5 text-sm font-medium text-ivory-100 transition-colors hover:bg-ink-700" data-id="element-53" data-mp="2u7gp2wevc8ge">
                Search
              </button>
            </form>

            <div className="mt-6 flex items-center gap-3 text-sm text-ink-500" data-id="element-54" data-mp="2kbdlrn8a63rv">
              <div className="flex text-gold" data-id="element-55" data-mp="2fhhpqqbkuqzc">
                {Array.from({
                length: 5
              }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-current" data-id="element-56" data-mp="2ahn0er7ga9ge" data-mpi={i} />)}
              </div>
              <span data-id="element-57" data-mp="26gb648dmt01v">Trusted by 2,400+ leaders across 38 countries</span>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-6" data-id="element-58" data-mp="1wk8370aygxdv">
          <motion.div className="relative" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.9,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.1
        }} data-id="element-59" data-mp="1wkgjz3a78muc">
            <div className="absolute -left-4 -top-4 hidden h-24 w-24 rounded-tl-3xl border-l-2 border-t-2 border-gold sm:block" data-id="element-60" data-mp="m03mbqf6ifhk" />
            <div className="overflow-hidden rounded-3xl shadow-lift" data-id="element-61" data-mp="q1ypl0go72xz">
              <img src={heroImage} alt="A senior executive in a modern boardroom" className="h-[420px] w-full object-cover sm:h-[540px]" data-id="element-62" data-mp="3pgsicih4y6sz" />
            </div>
            <motion.div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-ink/10 bg-ivory-50 p-5 shadow-lift sm:block" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.6
          }} data-id="element-63" data-mp="2bkwr7q9h02c">
              <p className="font-serif text-3xl text-ink" data-id="element-64" data-mp="3aquts1fanf39">96%</p>
              <p className="mt-1 text-xs text-ink-500" data-id="element-65" data-mp="2rn1d13rrahqc">Placement retention at two years</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="border-y border-ink/10 bg-ivory-200/60" data-id="element-66" data-mp="2mq15eof9d6x4">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-6 lg:grid-cols-4 lg:px-10" data-id="element-67" data-mp="31e5se4lcg4lp">
          {stats.map((s, i) => <motion.div key={s.label} className="py-8 text-center lg:py-10" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.08,
          duration: 0.5
        }} data-id="element-68" data-mp="2wl2xnt4typ3x" data-mpi={i}>
              <p className="font-serif text-3xl text-ink lg:text-4xl" data-id="element-69" data-mp="3k9m4s5c58vuf">{s.value}</p>
              <p className="mt-2 text-xs uppercase tracking-widest2 text-ink-500" data-id="element-70" data-mp="3fdn7ttcr25ie">
                {s.label}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}